/**
 * UserPicker
 *
 * Searchable combobox for selecting an internal user (Account Director,
 * Scoping Lead, Delivery Lead, Reviewer, etc.) with a free-text fallback for
 * people who don't have a user account in the system (e.g. third-party leads).
 *
 * The component is "controlled by both id and name":
 *   - userId  : foreign key to users.id (null when free-text or unset)
 *   - userName: display string (always set when there is anything to show)
 *
 * onChange returns BOTH so the caller writes them to the API in one PATCH —
 * keeping the denormalised _name column in sync with the FK _id column.
 *
 * Users with the `admin.users.manage` permission (including super admins) see
 * a "+ Add new person" action that opens AddUserModal to pre-provision a stub
 * user; the new user is auto-selected on success.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X, Search, UserPlus, Mail, Type, Check } from 'lucide-react';
import { useUserLookup, type UserLookupItem } from '@/hooks/useUserLookup';
import { useHasPermission } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { AddUserModal } from './AddUserModal';

interface UserPickerProps {
  /** Foreign-key user id, or null when free-text / unset */
  userId: string | null;
  /** Display name — denormalised text, always editable as fallback */
  userName: string | null;
  /** Called with both id and name on every change */
  onChange: (next: { userId: string | null; userName: string | null }) => void;
  /** Field label shown above the input (optional) */
  label?: string;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Allow free-text fallback for non-internal people (default: true) */
  allowFreeText?: boolean;
  /** Disable the field entirely */
  disabled?: boolean;
  /** Compact rendering (smaller padding) */
  compact?: boolean;
  /** Optional className for the outer wrapper */
  className?: string;
}

export function UserPicker({
  userId,
  userName,
  onChange,
  label,
  placeholder = 'Search by name or email…',
  allowFreeText = true,
  disabled = false,
  compact = false,
  className,
}: UserPickerProps) {
  const canManageUsers = useHasPermission('admin.users.manage');

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useUserLookup({ q: query });
  const users = data?.users || [];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Auto-clear query when closed
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const isLinked = !!userId;
  const hasValue = !!userName || !!userId;

  function selectUser(u: UserLookupItem) {
    onChange({ userId: u.id, userName: u.name });
    setOpen(false);
  }

  function useAsFreeText() {
    if (!query.trim()) return;
    onChange({ userId: null, userName: query.trim() });
    setOpen(false);
  }

  function clear() {
    onChange({ userId: null, userName: null });
  }

  function handleNewUserCreated(newUser: UserLookupItem) {
    setShowAddModal(false);
    selectUser(newUser);
  }

  // Show free-text option only if user typed something that doesn't exactly match a user
  const exactMatch = users.find(
    (u) => u.name.toLowerCase() === query.trim().toLowerCase()
  );
  const showFreeTextOption =
    allowFreeText && query.trim().length > 0 && !exactMatch;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      )}

      {/* Trigger button — shows current value */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-2 rounded-lg border bg-white text-left transition-colors',
          compact ? 'px-2.5 py-1.5 text-sm' : 'px-3 py-2 text-sm',
          disabled
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-xtravirt-cyan focus:border-xtravirt-cyan'
        )}
      >
        {hasValue ? (
          <>
            {isLinked ? (
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-xtravirt-cyan/15 text-xtravirt-cyan text-[10px] font-semibold flex-shrink-0">
                {(userName || '?').slice(0, 1).toUpperCase()}
              </span>
            ) : (
              <Type className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
            <span className="flex-1 truncate text-gray-900">{userName}</span>
            {!isLinked && allowFreeText && (
              <span className="text-[10px] uppercase font-semibold text-gray-400 flex-shrink-0">free text</span>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                title="Clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        ) : (
          <>
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="flex-1 text-gray-400">{placeholder}</span>
            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search users…"
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-xtravirt-cyan"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500">Loading…</div>
            ) : users.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500">
                {query ? 'No users match' : 'No users yet'}
              </div>
            ) : (
              users.map((u) => {
                const selected = u.id === userId;
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => selectUser(u)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors',
                      selected && 'bg-blue-50'
                    )}
                  >
                    <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-xtravirt-cyan/15 text-xtravirt-cyan text-xs font-semibold flex-shrink-0">
                      {u.name.slice(0, 1).toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-gray-900 truncate">{u.name}</p>
                        {!u.hasSignedIn && (
                          <span className="text-[9px] uppercase font-semibold text-amber-700 bg-amber-100 px-1 rounded flex-shrink-0">
                            Pending
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {u.email}
                      </p>
                    </div>
                    {selected && <Check className="h-4 w-4 text-xtravirt-cyan flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Free-text fallback option */}
          {showFreeTextOption && (
            <button
              type="button"
              onClick={useAsFreeText}
              className="w-full flex items-center gap-2 px-3 py-2 border-t border-gray-100 text-left text-sm hover:bg-gray-50 transition-colors"
            >
              <Type className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-gray-700 truncate">
                Use "<span className="font-medium">{query.trim()}</span>" as plain text
              </span>
              <span className="text-[10px] uppercase font-semibold text-gray-400">no link</span>
            </button>
          )}

          {/* Users with manage perm: add new user */}
          {canManageUsers && (
            <button
              type="button"
              onClick={() => {
                setShowAddModal(true);
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 border-t border-gray-100 text-left text-sm hover:bg-blue-50 text-xtravirt-cyan font-medium"
            >
              <UserPlus className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1">Add new person…</span>
            </button>
          )}
        </div>
      )}

      {/* Inline modal for creating a stub user */}
      {showAddModal && (
        <AddUserModal
          initialName={query.trim()}
          onClose={() => setShowAddModal(false)}
          onCreated={handleNewUserCreated}
        />
      )}
    </div>
  );
}
